/**
 * usePwaInstall — single source of truth for the PWA install lifecycle.
 *
 * Why: the `beforeinstallprompt` event fires exactly ONCE per tab, and
 * the event object is single-use. Multiple components (banner, login-
 * page button, settings page) need to react to it, so we capture the
 * event in a module-level singleton and broadcast state changes via a
 * tiny pub/sub.
 *
 * Returns:
 *   {
 *     canInstall,     // true on Chrome/Android/Edge after the browser fires beforeinstallprompt
 *     isiOS,          // true on iPhone/iPad — show "Add to Home Screen" instructions instead
 *     isStandalone,   // true if already installed and running stand-alone
 *     install,        // async () => "accepted" | "dismissed" | "unavailable"
 *   }
 */
import { useEffect, useState, useCallback } from "react";

let _stashedEvent = null;
let _isStandalone = false;
const _listeners = new Set();

function _notify() {
  for (const fn of _listeners) {
    try { fn(); } catch (_) { /* ignore */ }
  }
}

if (typeof window !== "undefined") {
  // Capture the install prompt as soon as the browser fires it. The
  // page's first React render will probably happen before this — that's
  // fine, the hook re-checks state on subscribe.
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    _stashedEvent = e;
    _notify();
  });
  window.addEventListener("appinstalled", () => {
    _stashedEvent = null;
    _isStandalone = true;
    _notify();
  });
  // Detect standalone via media query OR iOS Safari's vendor flag.
  try {
    _isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator?.standalone === true;
  } catch (_) { /* ignore */ }
}

function detectIOS() {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

export default function usePwaInstall() {
  const [, force] = useState(0);

  useEffect(() => {
    const sub = () => force((n) => n + 1);
    _listeners.add(sub);
    return () => _listeners.delete(sub);
  }, []);

  const install = useCallback(async () => {
    if (!_stashedEvent) return "unavailable";
    try {
      await _stashedEvent.prompt();
      const choice = await _stashedEvent.userChoice;
      // The event object is single-use; clear it either way so subsequent
      // calls correctly report "unavailable" instead of throwing.
      _stashedEvent = null;
      _notify();
      return choice?.outcome || "dismissed";
    } catch (_) {
      _stashedEvent = null;
      _notify();
      return "dismissed";
    }
  }, []);

  return {
    canInstall: Boolean(_stashedEvent),
    isiOS: detectIOS(),
    isStandalone: _isStandalone,
    install,
  };
}
